import React, { useState, useEffect } from 'react'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Card from '@/shared/Card'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'
import Loading from '@/shared/Loading'
import useModal from '@/hooks/useModalV2'
import Modal from '@/shared/Modal'
import { Link } from 'react-router-dom'

// NOTE: Ads are intended to promote projects, members, or submissions, 
// but they can be used for anything subject to approval.

const AdvertisingCampaigns = () => {
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const { getRequest, putpostRequest } = useQuery()
  const { openModal, closeModal, isOpen } = useModal()
  
  const [loading, setLoading] = useState(true)
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState(null)

  useEffect(() => {
    fetchCampaigns()
  }, [])

  const fetchCampaigns = async () => {
    setLoading(true)
    try {
      getRequest('/api/v3/ads', {}, (err, data) => {
        if (err) {
          setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
            <p className="mt-1 text-sm dark:text-red-300 text-red-400">Failed to load campaigns</p>
          </div>)
          return
        }
        setCampaigns(data.ads || [])
        setLoading(false)
      })
    } catch (error) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">Failed to load campaigns</p>
      </div>)
      setLoading(false)
    }
  }

  const handlePauseCampaign = async (campaignId) => {
    try {
      putpostRequest(`/api/v3/ads/${campaignId}/pause`, 'POST', {}, (err, data) => {
        if (err) {
          setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
            <p className="mt-1 text-sm dark:text-red-300 text-red-400">Failed to pause campaign</p>
          </div>)
          return
        }
        
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-green-100 text-green-600">Success</p>
          <p className="mt-1 text-sm dark:text-green-300 text-green-400">Campaign paused successfully</p>
        </div>)
        
        fetchCampaigns() // Refresh the list
      })
    } catch (error) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">Failed to pause campaign</p>
      </div>)
    }
  }

  const handleUnpauseCampaign = async (campaignId) => {
    try {
      putpostRequest(`/api/v3/ads/${campaignId}/unpause`, 'POST', {}, (err, data) => {
        if (err) {
          setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
            <p className="mt-1 text-sm dark:text-red-300 text-red-400">Failed to unpause campaign</p>
          </div>)
          return
        }
        
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-green-100 text-green-600">Success</p>
          <p className="mt-1 text-sm dark:text-green-300 text-green-400">Campaign unpaused successfully</p>
        </div>)
        
        fetchCampaigns() // Refresh the list
      })
    } catch (error) {
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
        <p className="mt-1 text-sm dark:text-red-300 text-red-400">Failed to unpause campaign</p>
      </div>)
    }
  }

  const handlePreviewCampaign = (campaign) => {
    setSelectedCampaign(campaign)
    openModal()
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', text: 'Active' },
      'paused': { bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', text: 'Paused' },
      'pending': { bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', text: 'Pending' },
      'ended': { bg: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200', text: 'Ended' },
      'expired': { bg: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200', text: 'Expired' },
      'cancelled': { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', text: 'Cancelled' }
    }
    
    const config = statusConfig[status] || statusConfig['pending']
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg}`}>
        {config.text}
      </span>
    )
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const calculateCTR = (clicks, views) => {
    if (!views || views === 0) return '0.00%'
    return ((clicks / views) * 100).toFixed(2) + '%'
  }

  const getPlacementDimensions = (placement) => {
    return placement === 'leaderboard' ? { width: 728, height: 90 } : { width: 250, height: 250 }
  }

  if (loading) {
    return <Loading full={true} />
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Your Campaigns</h1>
          <p className="text-gray-600 dark:text-gray-300">Manage your advertising campaigns</p>
        </div>
        <Link to="/advertising/new">
          <PrimaryButton onClick={() => {}} text="New Campaign" />
        </Link>
      </div>

      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">About Advertising</h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Ads are intended to promote projects, members, or submissions, but they can be used for anything subject to approval.
            </p>
            <a href="/advertising" className="mt-2 inline-block text-sm font-medium text-blue-800 dark:text-blue-200 underline hover:no-underline">
              Are you looking for permanent placement and you represent a brand?
            </a>
          </div>
        </div>
      </div>

      {campaigns.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No campaigns yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first advertising campaign to start reaching your target audience.
            </p>
            <Link to="/advertising/new">
              <PrimaryButton text="Create Your First Campaign" />
            </Link>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Views Left
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Clicks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    CTR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded object-cover"
                            src={campaign.image_url}
                            alt={campaign.name}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {campaign.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {campaign.placement}
                            {/* What this campaign actually cost, straight from
                                the ad record. Business members are charged half
                                list, and until recently the stored figure was
                                list price regardless — so this used to be able
                                to disagree with the card statement. */}
                            {campaign.cost > 0 && <> · ${campaign.cost.toFixed(2)}</>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {campaign.views || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {Math.max(0, (campaign.impressions || 0) - (campaign.views || 0))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {campaign.clicks || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {calculateCTR(campaign.clicks, campaign.views)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(campaign.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatDate(campaign.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <DefaultButton
                        onClick={() => handlePreviewCampaign(campaign)}
                        text="Preview"
                        className="text-xs"
                      />
                      {campaign.status === 'active' && (
                        <DefaultButton
                          onClick={() => handlePauseCampaign(campaign.id)}
                          text="Pause"
                          className="text-xs"
                        />
                      )}
                      {campaign.status === 'paused' && (
                        <DefaultButton
                          onClick={() => handleUnpauseCampaign(campaign.id)}
                          text="Unpause"
                          className="text-xs"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Preview Modal */}
      <Modal
        isOpen={isOpen}
        closeModal={closeModal}
        title={selectedCampaign ? `${selectedCampaign.name} Preview` : 'Ad Preview'}
        icon="image"
        maxWidth="max-w-4xl"
      >
        {selectedCampaign && (
          <div className="mt-6">
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Preview of your {selectedCampaign.placement} ad:
                </p>
                
                <div className="flex justify-center">
                  {(() => {
                    const dimensions = getPlacementDimensions(selectedCampaign.placement)
                    return (
                      <div 
                        className="border-2 border-gray-300 dark:border-gray-600 rounded overflow-hidden bg-white dark:bg-gray-700"
                        style={{ 
                          width: `${dimensions.width}px`, 
                          height: `${dimensions.height}px`,
                          maxWidth: '100%'
                        }}
                      >
                        <a 
                          href={selectedCampaign.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block w-full h-full hover:opacity-90 transition-opacity cursor-pointer"
                        >
                          <img
                            src={selectedCampaign.image_url}
                            alt={selectedCampaign.name}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      </div>
                    )
                  })()}
                </div>
                
                <div className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>
                    <span className="font-medium">Target URL:</span>{' '}
                    <a 
                      href={selectedCampaign.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-cccblue-600 dark:text-cccblue-400 hover:underline"
                    >
                      {selectedCampaign.url}
                    </a>
                  </p>
                  <p><span className="font-medium">Placement:</span> {selectedCampaign.placement}</p>
                  <p><span className="font-medium">Dimensions:</span> {(() => {
                    const dimensions = getPlacementDimensions(selectedCampaign.placement)
                    return `${dimensions.width} × ${dimensions.height} pixels`
                  })()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default AdvertisingCampaigns
